/**
 * Live survey results — one renderer for two audiences (TRUS-624).
 *
 *   - PublicSurveyResultsPage: anyone with the survey's results link. Choice
 *     questions only; the server withholds free text and contact details, and
 *     shows nothing until enough people have answered.
 *   - Reports > Survey Results (SurveyResultsReport): staff, with the recent
 *     text answers as well.
 *
 * Shape comes from GET /api/public/forms/{token}/results and
 * GET /api/forms/templates/{id}/response-summary, which share it. Labels are
 * tenant-authored, so they go through the same dynamic translator the survey
 * itself uses — a Spanish respondent reads Spanish results.
 *
 * Percentages are of the people who answered THAT question: skip logic means
 * most questions are not asked of everyone. A check-all-that-apply question can
 * therefore add up to more than 100%, and says so.
 */

import React, { useState } from 'react';
import { MessageSquare, Users, Clock, Languages } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { DynamicTranslator } from './dynamicTranslation';

export interface SurveyOptionResult {
  value: string;
  label: string;
  count: number;
  pct: number;
  retired?: boolean;
}

export interface SurveyQuestionResult {
  question_id: string;
  question_text: string;
  question_type: string;
  response_count: number;
  multi_select?: boolean;
  max_selections?: number;
  options?: SurveyOptionResult[];
  /** Staff only: most recent text answers. */
  responses?: string[];
}

export interface SurveyResultsData {
  total_responses: number;
  last_response_at?: string | null;
  by_language?: Record<string, number>;
  by_day?: { date: string; count: number }[];
  sections: { section_id: string; title: string; questions: SurveyQuestionResult[] }[];
  generated_at?: string;
}

interface SurveyResultsViewProps {
  data: SurveyResultsData;
  tr: DynamicTranslator;
  /** Bar colour; the public page passes the org's theme primary. */
  accent?: string;
  showTextAnswers?: boolean;
}

const TEXT_PREVIEW = 5;

function formatWhen(iso: string | null | undefined, language: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(language === 'es' ? 'es-US' : 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const StatTile: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({
  icon,
  label,
  children,
}) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
      {icon}
      {label}
    </div>
    <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {children}
    </div>
  </div>
);

const TextAnswers: React.FC<{ answers: string[] }> = ({ answers }) => {
  const { t } = useAppTranslation();
  const [expanded, setExpanded] = useState(false);
  if (answers.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t('survey_results.no_text_answers', 'No answers yet.')}</p>;
  }
  const shown = expanded ? answers : answers.slice(0, TEXT_PREVIEW);
  return (
    <div>
      <ul className="space-y-1.5">
        {shown.map((answer, i) => (
          <li
            key={i}
            className="text-sm text-gray-700 dark:text-gray-300 border-l-2 border-gray-200 dark:border-gray-600 pl-3"
          >
            {answer}
          </li>
        ))}
      </ul>
      {answers.length > TEXT_PREVIEW && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          {expanded
            ? t('survey_results.show_fewer', 'Show fewer')
            : t('survey_results.show_all_answers', `Show all ${answers.length}`, { count: answers.length })}
        </button>
      )}
    </div>
  );
};

export const SurveyResultsView: React.FC<SurveyResultsViewProps> = ({
  data,
  tr,
  accent = '#2563EB',
  showTextAnswers = false,
}) => {
  const { t, language } = useAppTranslation();
  const languages = data.by_language || {};
  const days = data.by_day || [];
  const peak = Math.max(1, ...days.map((d) => d.count));
  const languageName = (code: string) =>
    code === 'es'
      ? t('survey_results.language_es', 'Spanish')
      : code === 'en'
        ? t('survey_results.language_en', 'English')
        : t('survey_results.language_other', 'Other');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile icon={<Users className="w-3.5 h-3.5" aria-hidden="true" />} label={t('survey_results.responses', 'Responses')}>
          {data.total_responses.toLocaleString()}
        </StatTile>
        <StatTile icon={<Clock className="w-3.5 h-3.5" aria-hidden="true" />} label={t('survey_results.latest', 'Latest response')}>
          <span className="text-base">{formatWhen(data.last_response_at, language)}</span>
        </StatTile>
        <StatTile icon={<Languages className="w-3.5 h-3.5" aria-hidden="true" />} label={t('survey_results.by_language', 'By language')}>
          <span className="text-base">
            {Object.keys(languages).length === 0
              ? '—'
              : Object.entries(languages)
                  .sort((a, b) => b[1] - a[1])
                  .map(([code, n]) => `${languageName(code)} ${n}`)
                  .join(' · ')}
          </span>
        </StatTile>
      </div>

      {days.length > 1 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('survey_results.per_day', 'Responses per day')}
          </h3>
          <div className="flex items-end gap-1 h-16" role="img" aria-label={t('survey_results.per_day', 'Responses per day')}>
            {days.map((d) => (
              <div
                key={d.date}
                className="flex-1 min-w-[3px] rounded-t"
                style={{ height: `${Math.max(4, (d.count / peak) * 100)}%`, backgroundColor: accent }}
                title={`${d.date}: ${d.count}`}
              />
            ))}
          </div>
          <div className="mt-1 flex justify-between text-[11px] text-gray-400">
            <span>{days[0].date}</span>
            <span>{days[days.length - 1].date}</span>
          </div>
        </div>
      )}

      {data.sections.map((section) => (
        <section key={section.section_id} className="space-y-3">
          {/* On its own surface: the public page sits on the org's theme
              background, which may be a dark gradient. */}
          <h2 className="inline-block rounded-md bg-white dark:bg-gray-800 px-3 py-1 text-base font-semibold text-gray-900 dark:text-gray-100 shadow-sm">
            {tr.sectionTitle(section.section_id, section.title)}
          </h2>
          {section.questions.map((q) => {
            const field = { field_id: q.question_id };
            return (
              <article
                key={q.question_id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
              >
                <header className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {tr.label(field, q.question_text)}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {t('survey_results.answered_count', `${q.response_count} answered`, { count: q.response_count })}
                  </span>
                </header>

                {q.options ? (
                  <ul className="space-y-2">
                    {q.options.map((opt) => (
                      <li key={opt.value}>
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="text-gray-700 dark:text-gray-300">
                            {tr.option(field, opt.value, opt.label)}
                            {opt.retired && (
                              <span className="ml-1 text-xs text-gray-400">
                                ({t('survey_results.retired_option', 'no longer offered')})
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 text-gray-600 dark:text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {opt.count} · {opt.pct}%
                          </span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${Math.min(100, opt.pct)}%`, backgroundColor: accent }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : showTextAnswers ? (
                  <div className="flex gap-2">
                    <MessageSquare className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
                    <div className="flex-1">
                      <TextAnswers answers={q.responses || []} />
                    </div>
                  </div>
                ) : null}

                {q.multi_select && (
                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    {t(
                      'survey_results.multi_select_note',
                      'People could choose more than one answer, so percentages can add up to more than 100%.',
                    )}
                  </p>
                )}
              </article>
            );
          })}
        </section>
      ))}
    </div>
  );
};

export default SurveyResultsView;
